// src/ml/estimateHumanLandmarks.js
import { Pose } from "@mediapipe/pose";

let pose = null;
let initPromise = null;

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

// MediaPipe singleton (init once per page load)
async function getPose() {
  if (pose) return pose;
  if (initPromise) return initPromise;

  initPromise = new Promise((resolve) => {
    const p = new Pose({
      locateFile: (file) =>
        `https://cdn.jsdelivr.net/npm/@mediapipe/pose/${file}`,
    });

    p.setOptions({
      modelComplexity: 1,
      smoothLandmarks: true,
      enableSegmentation: false,
      smoothSegmentation: false,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });

    pose = p;
    resolve(p);
  });

  return initPromise;
}

function pickBestPose(results) {
  const lms = results?.poseLandmarks;
  if (!lms || lms.length < 33) return null;

  // A simple confidence estimate: average visibility of major joints
  const idx = {
    nose: 0,
    lShoulder: 11,
    rShoulder: 12,
    lHip: 23,
    rHip: 24,
    lKnee: 25,
    rKnee: 26,
    lAnkle: 27,
    rAnkle: 28,
  };

  const vis = (i) => (lms[i]?.visibility ?? 0);
  const conf =
    (vis(idx.nose) +
      vis(idx.lShoulder) +
      vis(idx.rShoulder) +
      vis(idx.lHip) +
      vis(idx.rHip) +
      vis(idx.lKnee) +
      vis(idx.rKnee) +
      vis(idx.lAnkle) +
      vis(idx.rAnkle)) /
    9;

  return { lms, conf };
}

function median(a, b) {
  if (a == null && b == null) return null;
  if (a == null) return b;
  if (b == null) return a;
  return (a + b) * 0.5;
}

function toPxY(lm, H) {
  if (!lm) return null;
  return lm.y * H;
}

/**
 * estimateHumanLandmarks({ imageUrl })
 *
 * Returns a stable, FULL-BODY vertical frame even if the photo is cropped:
 * - bodyTopPx (head-ish)
 * - bodyBottomPx (feet-ish; inferred if not visible)
 * - landmarksNorm (0..1 from FEET..HEAD)
 */
export async function estimateHumanLandmarks({ imageUrl }) {
  const p = await getPose();

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = async () => {
      try {
        let lastResults = null;

        p.onResults((res) => {
          lastResults = res;
        });

        await p.send({ image: img });

        const best = pickBestPose(lastResults);
        if (!best) {
          // Fallback: assume full body is the image height
          resolve({
            confidence: 0,
            bodyTopPx: 0,
            bodyBottomPx: img.height,
            poseLandmarks: null,
            visible: {
              head: false,
              shoulders: false,
              hips: false,
              knees: false,
              feet: false,
            },
            landmarksNorm: {
              FEET: 0.0,
              KNEE: 0.25,
              MID_THIGH: 0.35,
              HIP: 0.45,
              WAIST: 0.55,
              CHEST: 0.7,
              SHOULDER: 0.82,
              HEAD: 1.0,
            },
          });
          return;
        }

        const { lms, conf } = best;

        const H = img.height;

        // landmark indices
        const NOSE = 0;
        const L_SHOULDER = 11,
          R_SHOULDER = 12;
        const L_HIP = 23,
          R_HIP = 24;
        const L_KNEE = 25,
          R_KNEE = 26;
        const L_ANKLE = 27,
          R_ANKLE = 28;

        const noseY = toPxY(lms[NOSE], H);
        const shY = median(toPxY(lms[L_SHOULDER], H), toPxY(lms[R_SHOULDER], H));
        const hipY = median(toPxY(lms[L_HIP], H), toPxY(lms[R_HIP], H));
        const kneeY = median(toPxY(lms[L_KNEE], H), toPxY(lms[R_KNEE], H));
        const ankleY = median(toPxY(lms[L_ANKLE], H), toPxY(lms[R_ANKLE], H));

        const vis = (i) => (lms[i]?.visibility ?? 0);

        const visible = {
          head: vis(NOSE) > 0.4,
          shoulders: vis(L_SHOULDER) > 0.4 || vis(R_SHOULDER) > 0.4,
          hips: vis(L_HIP) > 0.4 || vis(R_HIP) > 0.4,
          knees: vis(L_KNEE) > 0.4 || vis(R_KNEE) > 0.4,
          feet: vis(L_ANKLE) > 0.4 || vis(R_ANKLE) > 0.4,
        };

        // ---- Determine bodyTopPx ----
        // Prefer nose; fallback to shoulder - small offset
        let bodyTopPx = noseY;
        if (bodyTopPx == null && shY != null) bodyTopPx = shY - 0.22 * H;
        if (bodyTopPx == null) bodyTopPx = 0;
        bodyTopPx = clamp(bodyTopPx, 0, H);

        // ---- Determine bodyBottomPx ----
        // Prefer ankles. If missing, infer using typical proportions from hips/knees.
        let bodyBottomPx = ankleY;

        // Typical ratios (rough but stable):
        // hip->knee ~ 0.23 of height
        // knee->ankle ~ 0.23 of height
        // hip->ankle ~ 0.46 of height
        if (bodyBottomPx == null) {
          if (kneeY != null && hipY != null) {
            const hipToKnee = Math.max(1, kneeY - hipY);
            bodyBottomPx = kneeY + hipToKnee; // assume knee->ankle ~= hip->knee
          } else if (hipY != null && shY != null) {
            const torso = Math.max(1, hipY - shY);
            bodyBottomPx = hipY + torso * 1.05; // legs about ~1x torso in many fashion shots
          } else if (hipY != null) {
            bodyBottomPx = hipY + 0.45 * H;
          } else {
            bodyBottomPx = H;
          }
        }

        bodyBottomPx = clamp(bodyBottomPx, bodyTopPx + 10, H);

        resolve({
          confidence: conf,
          poseLandmarks: lms,
          bodyTopPx,
          bodyBottomPx,
          visible,
          landmarksNorm: {
            FEET: 0.0,
            KNEE: 0.25,
            MID_THIGH: 0.35,
            HIP: 0.45,
            WAIST: 0.55,
            CHEST: 0.7,
            SHOULDER: 0.82,
            HEAD: 1.0,
          },
        });
      } catch (e) {
        reject(e);
      }
    };

    img.onerror = () => reject(new Error("Pose image failed to load: " + imageUrl));
    img.src = imageUrl;
  });
}
